class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.addToCartBtn = this.querySelector('[data-subscription-add-to-cart]');
    this.subscriptionOptions = Array.from(this.querySelectorAll('.subscription-option'));
    this.selectedOption = null;
  }

  connectedCallback() {
    if (!this.addToCartBtn) return;
    
    // Initialize with the first option (one-time purchase) selected
    this.selectedOption = this.subscriptionOptions.find(option => option.classList.contains('subscription-option--selected'));
    if (!this.selectedOption) {
      this.selectedOption = this.subscriptionOptions[0];
    }
    
    // Add click handlers to all subscription options
    this.subscriptionOptions.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
    });
    
    this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
  }

  handleOptionClick(event) {
    // Remove selected class from all options
    this.subscriptionOptions.forEach(option => {
      option.classList.remove('subscription-option--selected');
    });
    
    // Add selected class to clicked option
    event.currentTarget.classList.add('subscription-option--selected');
    
    // Update selected option
    this.selectedOption = event.currentTarget;
  }

  handleAddToCart() {
    console.log('Add to cart clicked');
    console.log('Selected option:', this.selectedOption);
    console.log('All options:', this.subscriptionOptions);
    
    if (!this.selectedOption) {
      console.error('No purchase option selected');
      return;
    }
    
    const button = this.addToCartBtn;
    const buttonText = button.querySelector('span');
    const originalText = buttonText.textContent;
    
    // Show loading state
    button.classList.add('loading');
    button.disabled = true;
    buttonText.textContent = 'Adding...';
    
    // Determine which product to add
    let productId = null;
    let sellingPlanId = null;
    
    const optionType = this.selectedOption.dataset.optionType;
    
    // Debug the selected option
    console.log('Selected option:', this.selectedOption);
    console.log('Option dataset:', this.selectedOption.dataset);
    
    if (optionType === 'onetime') {
      // Add main product
      productId = this.selectedOption.dataset.productId;
      sellingPlanId = this.selectedOption.dataset.sellingPlanId;
    } else {
      // For subscription products, get the product ID and selling plan ID
      productId = this.selectedOption.dataset.productId;
      sellingPlanId = this.selectedOption.dataset.sellingPlanId;
      
      // Check if there's a checkbox product selected
      const checkboxInput = this.selectedOption.querySelector('input[type="checkbox"]:checked');
      
      if (checkboxInput) {
        // Add checkbox product with its own selling plan
        productId = checkboxInput.value;
        sellingPlanId = checkboxInput.dataset.sellingPlanId || null;
      }
    }
    
    if (!productId || productId === 'undefined' || productId === 'null') {
      console.error('No valid product ID found:', productId);
      this.resetButton(button, buttonText, originalText);
      return;
    }
    
    // Validate product ID is a number
    const numericProductId = parseInt(productId);
    if (isNaN(numericProductId)) {
      console.error('Product ID is not a valid number:', productId);
      this.resetButton(button, buttonText, originalText);
      return;
    }
    
    // Use the numeric product ID
    productId = numericProductId;
    
    // Debug logging
    console.log('Adding to cart:', {
      productId: productId,
      sellingPlanId: sellingPlanId,
      optionType: optionType
    });
    
    // Prepare the request body
    const requestBody = {
      id: productId,
      quantity: 1
    };
    
    // Only add selling_plan if it's valid
    if (sellingPlanId && sellingPlanId !== 'null' && sellingPlanId !== 'undefined') {
      const numericSellingPlanId = parseInt(sellingPlanId);
      if (!isNaN(numericSellingPlanId)) {
        requestBody.selling_plan = numericSellingPlanId;
      } else {
        console.warn('Invalid selling plan ID:', sellingPlanId);
      }
    }
    
    console.log('Request body:', requestBody);

    const giftNoteModalOpener = this.querySelector('[data-modal="#PopupModal-product-gift-note"]');
    if (giftNoteModalOpener) {
      const giftNoteModal = document.querySelector(giftNoteModalOpener.dataset.modal);
      if (giftNoteModal) {
        const idInput = giftNoteModal.querySelector('[name="items[][id]"]');
        const quantityInput = giftNoteModal.querySelector('[name="items[][quantity]"]');
        const attachedToInput = giftNoteModal.querySelector('[name="items[][properties][AttachedTo]"]');
        const giftMessageInput = giftNoteModal.querySelector('[name="items[][properties][GiftMessage]"]');
        if (idInput && quantityInput && attachedToInput && giftMessageInput && giftMessageInput.getAttribute('form')) {
          requestBody.items = [
            {
              id: idInput.value,
              quantity: quantityInput.value,
              properties: {
                AttachedTo: attachedToInput.value,
                GiftMessage: giftMessageInput.value
              }
            }
          ];
        }
      }
    }

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.text().then(text => {
        console.log('Raw response text:', text);
        if (!text || text.trim() === '') {
          console.error('Empty response body received');
          throw new Error('Empty response from server');
        }
        return JSON.parse(text);
      });
    })
    .then(data => {
      console.log('Response data:', data);
      if (data.status === 422) {
        throw new Error(data.description || 'Product could not be added to cart');
      }
      
      // Success - trigger cart drawer refresh and open
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh', {
        detail: { 'response': data },
        bubbles: true,
        composed: true
      }));
      
      // Directly open the cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.toggleDrawerClasses) {
        // If drawer is not open, open it
        if (!cartDrawer.isOpen) {
          cartDrawer.toggleDrawerClasses();
        }
      }
      
      // Also try the pubsub approach as fallback
      if (typeof window.publish !== 'undefined' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: "subscription-widget",
          productVariantId: productId,
          cartData: data,
        });
      } else if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "subscription-widget",
          productVariantId: productId,
          cartData: data,
        });
      }
      
      // Show success message
      buttonText.textContent = 'Added!';
      setTimeout(() => {
        this.resetButton(button, buttonText, originalText);
      }, 1500);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      buttonText.textContent = 'Error';
      setTimeout(() => {
        this.resetButton(button, buttonText, originalText);
      }, 2000);
    });
  }
  
  resetButton(button, buttonText, originalText) {
    button.classList.remove('loading');
    button.disabled = false;
    buttonText.textContent = originalText;
  }
}

customElements.define('subscription-widget', SubscriptionWidget);
